import AppForms from "@toothfairy/shared-ui/AppForms";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppExpander from "@toothfairy/shared-ui/AppExpander";
import AppTable from "@toothfairy/shared-ui/AppTable";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { View, useWindowDimensions } from "react-native";
import dayjs from "dayjs";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyChoice from "../../../../Components/ToothFairyComponents/ToothFairyChoice";
import Constants from "../../../../Constants";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { isLargeScreen } from "../../../MainViewContainer";
import validations from "../Functions/validations";
import connectionsHelper from "../../../../API/Connections/connectionsHelper";
import entitySnapshotsHelper from "../../../../API/EntitySnapshots/entitySnapshotsHelper";
import IdCopyView from "../../../../Components/ToothFairyComponents/IdCopyView";
import authorisationsHelper from "../../../../API/Authorisations/authorisationsHelper";
import execEnvsHelper from "../../../../API/ExecutionEnvironments/execEnvsHelper";
import AppUtilities from "@toothfairy/shared-ui/AppUtilities";
import Color from "../../../../Utility/Color";
import { AppIcon, AppText } from "@toothfairy/shared-ui";
import objectDiffUtils from "../../../../Utils/objectDiffUtils";
import semverHelper from "../../../../Utils/semverHelper";
import { Popable } from "react-native-popable";
import VersionDiffSummary from "../../../../Components/VersionComparison/VersionDiffSummary";
import VersionDiffView from "../../../../Components/VersionComparison/VersionDiffView";
import RelationshipPanel from "./RelationshipPanel";
import ToolTestModal from "./ToolTestModal";
import {
  BuilderBareColumn,
  BuilderSection,
  PassthroughSection,
  useBuilderDirtyTracking,
} from "./BuilderSections";

const TOOL_SECTION_FIELD_MAP = {
  profile: ["name", "description"],
  endpoint: [
    "url",
    "requestType",
    "graphqlQuery",
    "graphqlMutation",
    "graphqlOperationName",
    "scope",
  ],
  auth: ["authorisationType"],
  parameters: ["parameters", "staticArgs", "headers"],
  behaviour: ["model"],
};

const FunctionSettingsAIView = ({
  isEdit,
  settingType,
  handleDeletion,
  initialMapValues,
  isBuilderLayout,
  builderActiveSection,
  onBuilderContextChange,
  saveVersion,
}) => {
  const { t } = useTranslation();
  const { currentTheme, mode } = AppTheme.useTheme();
  const [availableConnections, setAvailableConnections] = React.useState([]);
  const [customCodeExecutionEnvironments, setCustomCodeExecutionEnvironments] =
    React.useState([]);
  const [availableAuthorisations, setAvailableAuthorisations] = React.useState(
    []
  );
  const { height } = useWindowDimensions();
  const entityName = "Tools mapping";
  const AppToasty = AppToast.useToast();
  const [toolSnapshots, setToolSnapshots] = useState([]);
  const [isLoadingSnapshots, setIsLoadingSnapshots] = useState(false);
  const [hasLoadedSnapshots, setHasLoadedSnapshots] = useState(false);
  const [showRollbackChoice, setShowRollbackChoice] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);
  const [rollbackData, setRollbackData] = useState(null);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [snapshotChangeCounts, setSnapshotChangeCounts] = useState({});
  const [showDiffSummary, setShowDiffSummary] = useState(false);
  const [showDiffView, setShowDiffView] = useState(false);
  const [currentDiffChanges, setCurrentDiffChanges] = useState(null);
  const [showTestModal, setShowTestModal] = useState(false);
  const {
    setFieldValue,
    values,
    initialValues,
    validationSchema,
    errors,
    submitCount,
  } = AppForms.useFormikContext();

  const _showBuilderSection = (sectionId) =>
    builderActiveSection == null || builderActiveSection === sectionId;
  const SectionRenderer = isBuilderLayout ? BuilderSection : PassthroughSection;
  const BareSectionRenderer = isBuilderLayout
    ? BuilderBareColumn
    : PassthroughSection;

  useBuilderDirtyTracking({
    isBuilderLayout,
    values,
    initialValues,
    saveVersion,
    onBuilderContextChange,
    errors,
    submitCount,
    sectionFieldMap: TOOL_SECTION_FIELD_MAP,
  });

  useEffect(() => {
    if (!isEdit) {
      if (!values?.functionVersion) setFieldValue("functionVersion", "0.1.0");
    } else if (!values?.functionVersion) {
      const suggested = semverHelper.suggestNext(
        initialMapValues?.functionVersion
      );
      setFieldValue("functionVersion", suggested);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, initialMapValues?.functionVersion]);
  const { state } = AppStateManager.useAppStateManager();

  // Helper constant for functions that only have name, description and script (no params, auth, etc.)
  const isScriptFunction = values?.isChatFn || values?.isAgentSkill;
  const { getActiveWorkspace, getWorkspaceUsers, getUserName } =
    useWorkspaces();
  const fetchAuthorisations = async () => {
    const authorisations = await authorisationsHelper.getAuthorisations(
      getActiveWorkspace()?.id
    );
    const _auth =
      authorisations?.data?.authorisationsByWorkspace?.items?.filter(
        (u) => u?._deleted != true
      ) || [];
    setAvailableAuthorisations(_auth);
  };
  const fetchConnections = async () => {
    const connections = await connectionsHelper.getDatabaseConnections(
      getActiveWorkspace()?.id
    );
    const _conn =
      connections?.data?.connectionsByWorkspace?.items?.filter(
        (u) => u?._deleted != true
      ) || [];
    setAvailableConnections(_conn);
    // Only set default value if no value is already set and we're not in edit mode
    if (_conn?.length > 0 && !values?.dbConnectionID && !isEdit) {
      setFieldValue("dbConnectionID", _conn[0]?.id);
    }
  };
  const _title = useCallback(() => {
    return isEdit ? t("settings.list.save") : t("settings.list.create");
  }, [entityName, isEdit, t]);

  const handleVersionPress = (item, index) => {
    const snapshot = toolSnapshots[index];
    if (snapshot?.snapshotData) {
      try {
        const parsedData = JSON.parse(snapshot.snapshotData);

        const differences = objectDiffUtils.calculateDifferences(
          parsedData,
          values,
          {
            excludeFields: [
              "id",
              "createdAt",
              "updatedAt",
              "_version",
              "_deleted",
              "_lastChangedAt",
              "workspaceID",
            ],
          }
        );

        setRollbackData(parsedData);
        setSelectedSnapshot(snapshot);
        setCurrentDiffChanges(differences);
        setShowDiffSummary(true);
      } catch (error) {
        console.error("Failed to parse snapshot data:", error);
        AppToasty.show(t("tools.failedLoadHistory"), { type: "danger" });
      }
    }
  };

  const handleRollbackConfirm = () => {
    if (rollbackData) {
      const excludeFields = [
        "id",
        "createdAt",
        "updatedAt",
        "_version",
        "_deleted",
        "_lastChangedAt",
        "workspaceID",
      ];
      Object.keys(rollbackData).forEach((key) => {
        if (!excludeFields.includes(key)) {
          setFieldValue(key, rollbackData[key]);
        }
      });
      setHistoryExpanded(false);
      AppToasty.show(t("tools.historicalVersionLoaded"), {
        type: "info",
      });
    }
    setShowDiffSummary(false);
    setShowDiffView(false);
    setShowRollbackChoice(false);
    setRollbackData(null);
    setSelectedSnapshot(null);
    setCurrentDiffChanges(null);
  };

  const handleHistoryExpandChange = (isExpanded) => {
    setHistoryExpanded(isExpanded);
    handleSnapshotsExpandChange(isExpanded);
  };

  const handleSnapshotsExpandChange = async (isExpanded) => {
    if (isExpanded && !hasLoadedSnapshots && values?.id) {
      setIsLoadingSnapshots(true);
      try {
        const response = await entitySnapshotsHelper.getSnapshotsByEntity(
          values.id,
          {
            sortDirection: "DESC",
            limit: 50,
          }
        );
        const snapshots = response?.data?.snapshotsByEntity?.items || [];
        setToolSnapshots(snapshots);
        setHasLoadedSnapshots(true);

        const counts = {};
        snapshots.forEach((snapshot, index) => {
          if (snapshot?.snapshotData) {
            try {
              const parsedData = JSON.parse(snapshot.snapshotData);
              const differences = objectDiffUtils.calculateDifferences(
                parsedData,
                values,
                {
                  excludeFields: [
                    "id",
                    "createdAt",
                    "updatedAt",
                    "_version",
                    "_deleted",
                    "_lastChangedAt",
                    "workspaceID",
                  ],
                }
              );
              counts[index] = differences.totalChanges;
            } catch (e) {
              counts[index] = 0;
            }
          }
        });
        setSnapshotChangeCounts(counts);
      } catch (error) {
        console.error("Error loading tool snapshots:", error);
      }
      setIsLoadingSnapshots(false);
    }
  };

  const handleViewFullDiff = () => {
    setShowDiffSummary(false);
    setShowDiffView(true);
  };

  const handleDiffSummaryRollback = () => {
    setShowDiffSummary(false);
    handleRollbackConfirm();
  };

  const handleDiffSummaryClose = () => {
    setShowDiffSummary(false);
    setRollbackData(null);
    setSelectedSnapshot(null);
    setCurrentDiffChanges(null);
  };

  const colourStyles = {
    control: (styles) => ({
      ...styles,
      backgroundColor: currentTheme?.theme?.inputBackgroundColor,
      borderColor: currentTheme?.theme?.border_color,
      fontFamily: "normalFont",
      fontSize: currentTheme?.theme?.small_font_size,
    }),
    option: (styles, { data, isDisabled, isFocused, isSelected }) => {
      let color = new Color(data?.backgroundColor || currentTheme?.theme?.grey);
      return {
        ...styles,
        backgroundColor: isDisabled
          ? undefined
          : isSelected
          ? color
          : isFocused
          ? color.alpha(0.1).css()
          : undefined,
        color: isDisabled
          ? "#ccc"
          : isSelected
          ? currentTheme?.theme?.white
            ? "white"
            : "black"
          : data?.backgroundColor,
        cursor: isDisabled ? "not-allowed" : "default",

        ":active": {
          ...styles[":active"],
          backgroundColor: !isDisabled
            ? isSelected
              ? data?.backgroundColor
              : color.alpha(0.3).css()
            : undefined,
        },
      };
    },
    multiValue: (styles, { data }) => {
      let color = new Color(data?.backgroundColor || currentTheme?.theme?.grey);
      return {
        ...styles,
        backgroundColor: color.alpha(0.1).css(),
      };
    },
    multiValueLabel: (styles, { data }) => ({
      ...styles,
      color: data?.backgroundColor,
    }),
    multiValueRemove: (styles, { data }) => ({
      ...styles,
      color: data?.backgroundColor,
      ":hover": {
        backgroundColor: data?.backgroundColor,
        color: "white",
      },
    }),
    menu: (styles) => ({
      ...styles,
      backgroundColor: currentTheme?.theme?.inputBackgroundColor,
      borderColor: currentTheme?.theme?.lightGray,
      fontFamily: "normalFont",
      fontSize: currentTheme?.theme?.small_font_size,
    }),
  };

  useEffect(() => {
    if (values?.isDatabaseScript) {
      fetchConnections();
    }
    fetchAuthorisations();
  }, [values?.isDatabaseScript]);
  const loadCustomEnvironments = async () => {
    try {
      const result = await execEnvsHelper.getCustomCodeExecutionEnvironments(
        getActiveWorkspace()?.id
      );
      let functions =
        result?.data?.customCodeExecutionEnvironmentsByWorkspace?.items;
      functions =
        functions
          ?.map((u) => {
            if (u?._deleted !== true && u?.isTemplate == true)
              return {
                ...u,
              };
          })
          ?.filter((u) => u != null) || [];
      setCustomCodeExecutionEnvironments(functions);
      // setAvailableFunctionsLoaded(true);
      return functions;
    } catch {
      // setAvailableFunctionsLoaded(false);
      return [];
    }
  };
  useEffect(() => {
    if (values?.chatAction === "script") setFieldValue("isCallbackFn", false);
  }, [values?.chatAction]);
  useEffect(() => {
    if (values?.isCodeTemplate) loadCustomEnvironments();
  }, [values?.isCodeTemplate]);
  // const { setFieldValue, values } = AppForms.useFormikContext();
  // useEffect(() => {
  //   if (values?.sourceLanguage !== "en") setFieldValue("targetLanguage", "en");
  //   if (values?.targetLanguage !== "en") setFieldValue("sourceLanguage", "en");
  // }, [values]);
  return (
    <>
      <>
        <View>
          {_showBuilderSection("profile") && (
            <SectionRenderer
              title={t("tools.builderSectionProfile", {
                defaultValue: "Overview",
              })}
              icon={AppAssets.icons.agent}
            >
              {isEdit && <IdCopyView id={values?.id} />}
              <AppForms.AppFormField
                showTextInputLabel
                isMandatory
                wrapperInputStyle={{
                  paddingTop: 10,
                }}
                label={t("settings.list.name")}
                placeholder={t("tools.toolNamePlaceholder")}
                keyboardtype="default"
                textContextType="label"
                autoCorrect={false}
                name="name"
              />
              <AppForms.AppFormField
                showTextInputLabel
                isMandatory
                wrapperInputStyle={{
                  paddingTop: 10,
                }}
                multiline
                textContainer={{
                  minHeight: 120,
                  paddingVertical: 10,
                  marginHorizontal: 5,
                }}
                label={t("settings.list.description")}
                placeholder={t("tools.toolDescPlaceholder")}
                keyboardtype="default"
                textContextType="label"
                autoCorrect={false}
                name="description"
              />
            </SectionRenderer>
          )}
          {_showBuilderSection("endpoint") && (
            <SectionRenderer
              title={t("tools.builderSectionEndpoint", {
                defaultValue: "Connection",
              })}
              icon={AppAssets.icons.network}
            >
              {values?.isDatabaseScript ? (
                <AppForms.AppFormField
                  showTextInputLabel
                  isMandatory
                  wrapperInputStyle={{
                    paddingTop: 10,
                  }}
                  label={t("tools.storedProcedure")}
                  placeholder={t("tools.storedProcedurePlaceholder")}
                  keyboardtype="default"
                  textContextType="label"
                  autoCorrect={false}
                  name={"dbScript"}
                />
              ) : values?.isMCPServer ? (
                <AppForms.AppFormField
                  showTextInputLabel
                  isMandatory
                  wrapperInputStyle={{
                    paddingTop: 10,
                  }}
                  label={t("tools.mcpServerUrl")}
                  placeholder={t("tools.mcpServerUrlPlaceholder")}
                  keyboardtype="default"
                  textContextType="label"
                  autoCorrect={false}
                  name={"url"}
                />
              ) : values?.isHtmlFn ? (
                <AppForms.AppFormField
                  showTextInputLabel
                  isMandatory
                  wrapperInputStyle={{
                    paddingTop: 10,
                  }}
                  label={t("settings.list.url")}
                  placeholder={t("tools.htmlUrlPlaceholder")}
                  keyboardtype="default"
                  textContextType="label"
                  autoCorrect={false}
                  name={"htmlUrl"}
                />
              ) : isScriptFunction ? (
                <></>
              ) : values?.isCodeTemplate ? (
                <></>
              ) : (
                <>
                  <AppForms.AppFormField
                    showTextInputLabel
                    isMandatory
                    wrapperInputStyle={{
                      paddingTop: 10,
                    }}
                    label={t("settings.list.url")}
                    placeholder={t("tools.endpointUrlPlaceholder")}
                    keyboardtype="default"
                    textContextType="label"
                    autoCorrect={false}
                    name={"url"}
                  />
                  <AppForms.AppFormDropDown
                    isElevated={false}
                    dropDownStyle={{
                      borderColor: currentTheme?.theme?.border_color,
                      maxWidth: !isLargeScreen() && 250,
                    }}
                    showTextInputLabel
                    inputLabelStyle={{
                      marginLeft: 5,
                    }}
                    label={t("tools.requestType")}
                    isMandatory
                    name="requestType"
                    items={[
                      "GET",
                      "POST",
                      "PUT",
                      "DELETE",
                      "PATCH",
                      "GRAPHQL_QUERY",
                      "GRAPHQL_MUTATION",
                    ].map((item) => ({
                      id: item,
                      name: item
                        .replace("GRAPHQL_", "GRAPHQL ")
                        .replace("_", " "),
                    }))}
                    propertyLabel="name"
                    propertyValue="id"
                  />
                  {values?.requestType == "GET" && (
                    <AppForms.AppFormSwitch
                      wrapperStyle={[
                        {
                          backgroundColor: isEdit
                            ? currentTheme?.theme?.grey
                            : currentTheme?.theme?.lightGrey,
                          maxHeight: 30,
                          paddingHorizontal: 10,
                          paddingVertical: 18,
                          margin: 5,
                          marginTop: 10,
                          marginBottom: 10,
                        },
                      ]}
                      labelTextStyle={{
                        fontSize: currentTheme?.theme?.small_font_size,
                        color: currentTheme?.theme?.darkGrey,
                      }}
                      showTextInputLabel={true}
                      label={t("tools.paramsAsPath")}
                      displayIsTicked={true}
                      name="paramsAsPath"
                      value={values?.paramsAsPath}
                      description={t("tools.paramsAsPathDesc")}
                      descriptionIcon={false}
                      descriptionColor={currentTheme?.theme?.mediumGrey}
                    />
                  )}
                  {values?.requestType === "GRAPHQL_QUERY" && (
                    <AppForms.AppFormField
                      showTextInputLabel
                      wrapperInputStyle={{
                        paddingTop: 10,
                      }}
                      label={t("tools.graphqlQuery")}
                      placeholder={t("tools.graphqlQueryPlaceholder")}
                      keyboardtype="default"
                      textContextType="label"
                      autoCorrect={false}
                      name={"graphqlQuery"}
                      multiline={true}
                      numberOfLines={4}
                      textContainer={{
                        marginHorizontal: 5,
                        padding: 10,
                        minHeight: 100,
                      }}
                    />
                  )}
                  {values?.requestType === "GRAPHQL_MUTATION" && (
                    <AppForms.AppFormField
                      showTextInputLabel
                      wrapperInputStyle={{
                        paddingTop: 10,
                      }}
                      label={t("tools.graphqlMutation")}
                      placeholder={t("tools.graphqlMutationPlaceholder")}
                      keyboardtype="default"
                      textContextType="label"
                      autoCorrect={false}
                      name={"graphqlMutation"}
                      multiline={true}
                      numberOfLines={4}
                      textContainer={{
                        marginHorizontal: 5,
                        padding: 10,
                        minHeight: 100,
                      }}
                    />
                  )}
                  {(values?.requestType === "GRAPHQL_QUERY" ||
                    values?.requestType === "GRAPHQL_MUTATION") && (
                    <AppForms.AppFormField
                      showTextInputLabel
                      wrapperInputStyle={{
                        paddingTop: 10,
                      }}
                      label={t("tools.graphqlOperationName")}
                      placeholder={t("tools.graphqlOperationNamePlaceholder")}
                      keyboardtype="default"
                      textContextType="label"
                      autoCorrect={false}
                      name={"graphqlOperationName"}
                    />
                  )}
                </>
              )}
            </SectionRenderer>
          )}
          {_showBuilderSection("auth") && (
            <SectionRenderer
              title={t("tools.builderSectionAuth", {
                defaultValue: "Authorisation",
              })}
              icon={AppAssets.icons.credentials}
            >
              {!isScriptFunction &&
                !values?.isDatabaseScript &&
                !values?.isCodeTemplate &&
                !values?.isHtmlFn && (
                  <>
                    <AppForms.AppFormDropDown
                      isElevated={false}
                      dropDownStyle={{
                        borderColor: currentTheme?.theme?.border_color,
                        maxWidth: !isLargeScreen() && 250,
                      }}
                      inputLabelStyle={{
                        marginLeft: 5,
                      }}
                      showTextInputLabel
                      label={t("tools.authorisationType")}
                      isMandatory
                      name="authorisationType"
                      items={["BEARER", "APIKEY", "OAUTH", "BASIC", "NONE"].map(
                        (item) => ({
                          id: item,
                          name: item,
                        })
                      )}
                      propertyLabel="name"
                      propertyValue="id"
                    />
                    {values?.authorisationType === "BASIC" && (
                      <AppText
                        style={{
                          color: currentTheme?.theme?.orange,
                          fontSize: currentTheme?.theme?.small_font_size,
                          marginTop: 8,
                          fontStyle: "italic",
                        }}
                        wrapperStyle={{
                          marginLeft: 10,
                        }}
                      >
                        Warning: Basic Auth transmits credentials in base64
                        encoding which can be easily decoded. Only use this
                        method over encrypted HTTPS/TLS connections.
                      </AppText>
                    )}
                    {values?.authorisationType !== "NONE" && (
                      <AppForms.AppFormDropDown
                        isElevated={false}
                        showTextInputLabel
                        wrapperInputStyle={{
                          paddingTop: 10,
                        }}
                        inputLabelStyle={{
                          marginLeft: 5,
                        }}
                        items={[
                          { id: "none", name: "none" },
                          ...availableAuthorisations?.filter(
                            (u) =>
                              u?.type ===
                              values?.authorisationType?.toLowerCase()
                          ),
                        ]}
                        label={t("tools.authorisation")}
                        placeholder={t("tools.authorisationPlaceholder")}
                        keyboardtype="default"
                        textContextType="label"
                        autoCorrect={false}
                        name="authorisationID"
                        hideShowIcon={true}
                        propertyLabel="name"
                        propertyValue="id"
                      />
                    )}
                    <AppForms.AppFormFieldJSONV2
                      showTextInputLabel
                      wrapperInputStyle={{
                        paddingTop: 10,
                      }}
                      textInputLabelStyle={{
                        marginLeft: 10,
                      }}
                      mode="code"
                      darkMode={mode === "dark"}
                      label={t("tools.additionalHeaders")}
                      placeholder={t("tools.additionalHeadersPlaceholder")}
                      name="headers"
                    />
                    <AppForms.AppFormFieldJSONV2
                      showTextInputLabel
                      wrapperInputStyle={{
                        paddingTop: 10,
                      }}
                      textInputLabelStyle={{
                        marginLeft: 10,
                      }}
                      mode="code"
                      formFieldContainerStyle={{
                        zIndex: -1,
                      }}
                      darkMode={mode === "dark"}
                      label={t("tools.staticArguments")}
                      placeholder={t("tools.staticArgumentsPlaceholder")}
                      name="staticArgs"
                    />
                  </>
                )}
            </SectionRenderer>
          )}

          {_showBuilderSection("parameters") && (
            <SectionRenderer
              title={t("tools.builderSectionParameters", {
                defaultValue: "Parameters",
              })}
              icon={AppAssets.icons.experiment}
            >
              {!values?.isHtmlFn &&
                !isScriptFunction &&
                !values?.isMCPServer && (
                  <AppForms.AppFormFieldJSONV2
                    showTextInputLabel
                    isMandatory
                    wrapperInputStyle={{
                      paddingTop: 10,
                    }}
                    textInputLabelStyle={{
                      marginLeft: 10,
                    }}
                    wrapperContainer={{
                      height: 450,
                    }}
                    mode="code"
                    schema={validations.parametersVal}
                    label={t("tools.parameters")}
                    name="parameters"
                    darkMode={mode === "dark"}
                  />
                )}
            </SectionRenderer>
          )}
          {_showBuilderSection("endpoint") && (
            <BareSectionRenderer>
              {values?.isChatFn && (
                <AppForms.AppFormField
                  showTextInputLabel
                  isMandatory
                  name="chatScript"
                  label={t("tools.chatScript")}
                  placeholder={t("tools.chatScriptPlaceholder")}
                  multiline={true}
                  numberOfLines={4}
                  style={{
                    marginTop: 2,
                  }}
                  wrapperInputStyle={{
                    marginBottom: 10,
                    marginTop: 10,
                  }}
                  textContainer={{
                    marginHorizontal: 5,
                    padding: 10,
                    minHeight: 120,
                  }}
                  type="text"
                />
              )}
              {values?.isAgentSkill && (
                <AppForms.AppFormField
                  showTextInputLabel
                  isMandatory
                  name="agentSkillScript"
                  label={t("tools.agentSkillScript")}
                  placeholder={t("tools.agentSkillScriptPlaceholder")}
                  multiline={true}
                  numberOfLines={8}
                  style={{
                    marginTop: 2,
                  }}
                  wrapperInputStyle={{
                    marginBottom: 10,
                    marginTop: 10,
                  }}
                  textContainer={{
                    marginHorizontal: 5,
                    padding: 10,
                    minHeight: 300,
                  }}
                  maxCharacters={8000}
                  type="text"
                />
              )}
              {values?.isCodeTemplate && (
                <AppForms.AppFormDropDown
                  isElevated={false}
                  dropDownStyle={{
                    borderColor: currentTheme?.theme?.border_color,
                    maxWidth: !isLargeScreen() && 250,
                  }}
                  inputLabelStyle={{
                    marginLeft: 5,
                  }}
                  showTextInputLabel
                  label={t("tools.executionHookTemplate")}
                  name="codeExecutionEnvironmentID"
                  items={[
                    {
                      id: null,
                      name: "none",
                    },
                    ...(customCodeExecutionEnvironments
                      ?.map((item) => {
                        return {
                          id: item?.id,
                          name: item?.name,
                        };
                      })
                      ?.filter((u) => u) || []),
                  ]}
                  propertyLabel="name"
                  propertyValue="id"
                />
              )}
            </BareSectionRenderer>
          )}
        </View>
        {_showBuilderSection("endpoint") && (
          <BareSectionRenderer>
            {values?.isDatabaseScript ? (
              <AppForms.AppFormDropDown
                isElevated={false}
                dropDownStyle={{
                  borderColor: currentTheme?.theme?.border_color,
                  maxWidth: !isLargeScreen() && 250,
                }}
                showTextInputLabel
                label={t("tools.connectionType")}
                isMandatory
                name="dbConnectionID"
                items={availableConnections?.map((item) => ({
                  id: item?.id,
                  name: `${item?.name} - ${item?.type?.toUpperCase()}`,
                }))}
                propertyLabel="name"
                propertyValue="id"
              />
            ) : values?.isMCPServer ? (
              <View
                style={{
                  zIndex: -1,
                }}
              />
            ) : values?.isHtmlFn ? (
              <View
                style={{
                  zIndex: -1,
                }}
              >
                <AppForms.AppFormSwitch
                  wrapperStyle={[
                    {
                      backgroundColor: isEdit
                        ? currentTheme?.theme?.grey
                        : currentTheme?.theme?.lightGrey,
                      maxHeight: 30,
                      paddingHorizontal: 10,

                      paddingVertical: 18,
                      margin: 5,
                      marginTop: 10,
                    },
                  ]}
                  labelTextStyle={{
                    fontSize: currentTheme?.theme?.small_font_size,
                    color: currentTheme?.theme?.darkGrey,
                  }}
                  showTextInputLabel={true}
                  label={t("tools.requiresRedirect")}
                  displayIsTicked={true}
                  name="isHtmlRedirectFn"
                  value={values?.isHtmlRedirectFn}
                  description={t("tools.requiresRedirectDesc")}
                  descriptionIcon={false}
                  descriptionColor={currentTheme?.theme?.mediumGrey}
                />
                <AppForms.AppFormField
                  showTextInputLabel
                  wrapperInputStyle={{
                    paddingTop: 10,
                  }}
                  label={t("tools.msgOnSuccessfulSubmit")}
                  placeholder={t("tools.msgOnSuccessfulSubmitPlaceholder")}
                  keyboardtype="default"
                  textContextType="label"
                  autoCorrect={false}
                  name="htmlFormOnSuccessfulSubmitMessage"
                />
                <AppForms.AppFormField
                  showTextInputLabel
                  wrapperInputStyle={{
                    paddingTop: 10,
                  }}
                  label={t("tools.msgOnFailedSubmit")}
                  placeholder={t("tools.msgOnFailedSubmitPlaceholder")}
                  keyboardtype="default"
                  textContextType="label"
                  autoCorrect={false}
                  name="htmlFormOnFailedSubmitMessage"
                />
              </View>
            ) : values?.isChatFn ? (
              <AppForms.AppFormDropDown
                isElevated={false}
                dropDownStyle={{
                  borderColor: currentTheme?.theme?.border_color,
                  maxWidth: !isLargeScreen() && 250,
                }}
                showTextInputLabel
                label={t("tools.chatActionType")}
                isMandatory
                name="chatAction"
                items={[
                  "suggestion",
                  "nextQuestion",
                  // "humanSupport",
                  // "redirection",
                  "script",
                ].map((item) => ({
                  id: item,
                  name: item,
                }))}
                propertyLabel="name"
                propertyValue="id"
              />
            ) : values?.isCodeTemplate || values?.isAgentSkill ? (
              <></>
            ) : (
              <></>
            )}
          </BareSectionRenderer>
        )}
      </>
      {_showBuilderSection("parameters") && (
        <BareSectionRenderer>
          {!values?.isHtmlFn &&
            !isScriptFunction &&
            !values?.isCodeTemplate &&
            !values?.isMCPServer && (
              <>
                <AppForms.AppFormField
                  showTextInputLabel
                  wrapperInputStyle={{
                    paddingTop: 10,
                  }}
                  label={t("settings.list.jsonPathExtractor")}
                  placeholder={t("settings.list.jsonPathExtractorPlaceholder")}
                  keyboardtype="default"
                  textContextType="label"
                  autoCorrect={false}
                  name="jsonPathExtractor"
                />
                <AppForms.AppFormDropDown
                  isElevated={false}
                  dropDownStyle={{
                    borderColor: currentTheme?.theme?.border_color,
                    maxWidth: !isLargeScreen() && 250,
                  }}
                  inputLabelStyle={{
                    marginLeft: 5,
                  }}
                  showTextInputLabel
                  label={t("settings.list.scope")}
                  name="scope"
                  items={[
                    {
                      id: "none",
                      name: t("common.none"),
                    },
                    {
                      id: "customerAuthentication",
                      name: t("tools.customerAuthentication"),
                    },
                    {
                      id: "caseAuthentication",
                      name: t("tools.caseAuthentication"),
                    },
                    {
                      id: "customer",
                      name: t("tools.customerRetrieval"),
                    },
                    {
                      id: "case",
                      name: t("tools.caseRetrieval"),
                    },
                  ]}
                  propertyLabel="name"
                  propertyValue="id"
                />
              </>
            )}
        </BareSectionRenderer>
      )}

      {_showBuilderSection("behaviour") && (
        <SectionRenderer
          title={t("tools.builderSectionBehaviour", {
            defaultValue: "Behaviour",
          })}
          icon={AppAssets.icons.bolt}
        >
          {getActiveWorkspace()?.subscriptionType != "base" &&
            !values?.isMCPServer &&
            !isScriptFunction && (
              <>
                <AppForms.AppFormDropDown
                  isElevated={false}
                  dropDownStyle={{
                    borderColor: currentTheme?.theme?.border_color,
                    maxWidth: !isLargeScreen() && 250,
                  }}
                  inputLabelStyle={{
                    marginLeft: 5,
                  }}
                  showTextInputLabel
                  label={t("tools.handOffAgentOnExecution")}
                  name="handOffAgent"
                  items={[
                    {
                      id: null,
                      name: t("common.none"),
                    },
                    ...(state?.agents
                      ?.map((item) => {
                        if (
                          item?.mode != "planner" &&
                          item?.mode != "computer" &&
                          item?.id != values?.handOffAgentOnCompletion
                        )
                          return {
                            id: item?.id,
                            name: item?.label,
                          };
                      })
                      ?.filter((u) => u) || []),
                  ]}
                  propertyLabel="name"
                  propertyValue="id"
                />
                {values?.handOffAgent && (
                  <>
                    <AppForms.AppFormSwitch
                      wrapperStyle={[
                        {
                          backgroundColor: isEdit
                            ? currentTheme?.theme?.grey
                            : currentTheme?.theme?.lightGrey,
                          maxHeight: 30,
                          paddingHorizontal: 10,

                          paddingVertical: 18,
                          margin: 5,
                          marginTop: 10,
                          marginBottom: 10,
                        },
                      ]}
                      labelTextStyle={{
                        fontSize: currentTheme?.theme?.small_font_size,
                        color: currentTheme?.theme?.darkGrey,
                      }}
                      showTextInputLabel={true}
                      label={t("tools.handOffToInitialAgentOnCompletion")}
                      displayIsTicked={true}
                      name="handOffToOriginalAgentOnCompletion"
                      value={values?.handOffToOriginalAgentOnCompletion}
                      description={t(
                        "tools.handOffToInitialAgentOnCompletionDesc"
                      )}
                      descriptionIcon={false}
                      descriptionColor={currentTheme?.theme?.mediumGrey}
                    />
                    {!values?.handOffToOriginalAgentOnCompletion && (
                      <AppForms.AppFormDropDown
                        isElevated={false}
                        dropDownStyle={{
                          borderColor: currentTheme?.theme?.border_color,
                          maxWidth: !isLargeScreen() && 250,
                        }}
                        inputLabelStyle={{
                          marginLeft: 5,
                        }}
                        showTextInputLabel
                        label={t("tools.handOffAgentOnCompletion")}
                        name="handOffAgentOnCompletion"
                        items={[
                          {
                            id: null,
                            name: t("common.none"),
                          },
                          ...(state?.agents
                            ?.map((item) => {
                              if (
                                item?.agentType === "Chatbot" &&
                                item?.mode != "planner" &&
                                item?.mode != "computer" &&
                                item?.id != values?.handOffAgent
                              )
                                return {
                                  id: item?.id,
                                  name: item?.label,
                                };
                            })
                            ?.filter((u) => u) || []),
                        ]}
                        propertyLabel="name"
                        propertyValue="id"
                      />
                    )}
                  </>
                )}
              </>
            )}
          {!values?.isHtmlFn && !isScriptFunction && !values?.isMCPServer && (
            <AppForms.AppFormSwitch
              wrapperStyle={[
                {
                  // backgroundColor: isEdit
                  //   ? currentTheme?.theme?.grey
                  //   : currentTheme?.theme?.lightGrey,
                  maxHeight: 30,
                  paddingHorizontal: 0,
                  width: "auto",
                  paddingVertical: 18,
                  margin: 5,
                  marginTop: 0,
                  marginRight: 5,
                },
              ]}
              labelTextStyle={{
                fontSize: currentTheme?.theme?.small_font_size,
                color: currentTheme?.theme?.darkGrey,
              }}
              showTextInputLabel={true}
              label={t("tools.callbackInvocation")}
              displayIsTicked={true}
              name="isCallbackFn"
              value={values?.isCallbackFn}
              disabled={[
                "customer",
                "case",
                "customerAuthentication",
                "caseAuthentication",
              ]?.includes(values?.scope)}
              description={t("tools.callbackInvocationDesc")}
              descriptionIcon={false}
              descriptionColor={currentTheme?.theme?.mediumGrey}
            />
          )}
          {getActiveWorkspace()?.subscriptionType != "base" &&
            !values?.isMCPServer &&
            !isScriptFunction && (
              <>
                <AppForms.AppFormSwitch
                  wrapperStyle={[
                    {
                      // backgroundColor: isEdit
                      //   ? currentTheme?.theme?.grey
                      //   : currentTheme?.theme?.lightGrey,
                      maxHeight: 30,
                      paddingHorizontal: 0,
                      width: "auto",
                      paddingVertical: 18,
                      margin: 5,
                      marginTop: 10,
                      marginBottom: 20,
                      marginRight: 5,
                    },
                  ]}
                  labelTextStyle={{
                    fontSize: currentTheme?.theme?.small_font_size,
                    color: currentTheme?.theme?.darkGrey,
                  }}
                  showTextInputLabel={true}
                  label={t("tools.handOffToHumanOnExecution")}
                  displayIsTicked={true}
                  name="handedOffToHumanOnCall"
                  value={values?.handedOffToHumanOnCall}
                  description={t("tools.handOffToHumanOnExecutionDesc")}
                  descriptionIcon={false}
                  descriptionColor={currentTheme?.theme?.mediumGrey}
                />
                {values?.handedOffToHumanOnCall && (
                  <>
                    <AppForms.AppFormMultiSelect
                      name="usersToHandOffTo"
                      topErrorOffset={-21}
                      touchedByDefault
                      placeholder={t("tools.selectUsersToNotify")}
                      hidePlaceholder={true}
                      items={getWorkspaceUsers()}
                      uniqueKey="id"
                      showDropDowns={true}
                      readOnlyHeadings={true}
                      style={{ maxWidth: 800 }}
                      wrapperContainerStyle={{
                        flex: !isLargeScreen() ? 1 : 0.5,
                        zIndex: 20,
                        paddingTop: 10,
                        marginBottom: 40,
                      }}
                      styles={colourStyles}
                      options={getWorkspaceUsers().map((u) => {
                        return {
                          ...u,
                          value: u?.id,
                          label: u?.username || u?.user_email,
                          category: "user",
                          backgroundColor: AppUtilities.stringToHslColor(
                            u?.username || u?.user_email,
                            71
                          ),
                        };
                      })}
                    />
                    <AppText
                      style={{
                        paddingLeft: 5,

                        fontSize: currentTheme?.theme?.extra_small_font_size,
                      }}
                      color={currentTheme?.theme?.orange}
                      wrapperStyle={{
                        top: -10,
                        justifyContent: "center",
                      }}
                      iconSize={12}
                      leftIcon={AppAssets?.icons?.question_icon}
                      iconColor={currentTheme?.theme?.orange}
                    >
                      Human hand-off when triggered overrides any agent hand-off
                      configuration
                    </AppText>
                  </>
                )}
              </>
            )}
        </SectionRenderer>
      )}

      {_showBuilderSection("usage") && (
        <SectionRenderer
          title={t("tools.builderSectionUsage", {
            defaultValue: "Usage",
          })}
          icon={AppAssets.icons.agent}
        >
          {isEdit && values?.id && (
            <RelationshipPanel
              isEdit={isEdit}
              dependsOn={[
                ...(values?.authorisationID
                  ? state?.authorisations
                      ?.filter((a) => a?.id === values.authorisationID)
                      .map((a) => ({
                        id: a.id,
                        name: a.name,
                        type: "authorisation",
                      })) || []
                  : []),
                ...(values?.codeExecutionEnvironmentID
                  ? (customCodeExecutionEnvironments || [])
                      .filter(
                        (e) => e?.id === values.codeExecutionEnvironmentID
                      )
                      .map((e) => ({
                        id: e.id,
                        name: e.name,
                        type: "hook",
                      }))
                  : []),
              ]}
              usedBy={
                state?.agents
                  ?.filter((a) => a?.agentFunctions?.includes(values?.id))
                  ?.map((a) => ({
                    id: a.id,
                    name: a.label || a.name,
                    type: "agent",
                  })) || []
              }
            />
          )}
        </SectionRenderer>
      )}

      {!isBuilderLayout && isEdit && values?.id && (
        <View style={{ marginTop: 10, marginBottom: 4 }}>
          <ToothFairyButton
            title={t("tools.testTool", { defaultValue: "Test" })}
            onPress={() => setShowTestModal(true)}
          />
        </View>
      )}

      {_showBuilderSection("versions") && (
        <SectionRenderer
          title={t("tools.builderSectionVersions", {
            defaultValue: "Versions",
          })}
          icon={AppAssets.icons.history}
        >
          {isEdit && (
            <View style={{ zIndex: -1 }}>
              <AppExpander
                style={{ minWidth: 250, zIndex: -1 }}
                title={t("tools.toolVersionsHistory")}
                icon={AppAssets.icons.history}
                onExpandedChange={handleHistoryExpandChange}
                isExpandedOnLoad={historyExpanded}
              >
                {isLoadingSnapshots ? (
                  <View style={{ padding: 20, alignItems: "center" }}>
                    <AppText>{t("tools.loadingHistory")}</AppText>
                  </View>
                ) : toolSnapshots?.length > 0 &&
                  getActiveWorkspace()?.subscriptionType != "base" &&
                  getActiveWorkspace()?.subscriptionType != "pro" ? (
                  <>
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                        paddingHorizontal: 10,
                        paddingVertical: 8,
                        marginBottom: 8,
                        backgroundColor:
                          currentTheme?.theme?.softBackgroundColor || "#f5f5f5",
                        borderRadius: 8,
                      }}
                    >
                      <AppText
                        style={{
                          fontSize: 13,
                          color: currentTheme?.theme?.fontColor,
                        }}
                      >
                        {t("dashboard.versioning.totalVersions", {
                          count: toolSnapshots?.length || 1,
                        })}
                      </AppText>
                      <View
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <AppText
                          style={{
                            fontSize: 13,
                            fontWeight: "600",
                            color: currentTheme?.theme?.primary,
                          }}
                        >
                          {t("dashboard.versioning.totalChanges", {
                            count: Object.values(snapshotChangeCounts)?.reduce(
                              (sum, count) => sum + count,
                              0
                            ),
                          })}
                        </AppText>
                      </View>
                    </View>
                    <AppTable
                      showPagination
                      itemsPerPage={50}
                      onRowPress={handleVersionPress}
                      style={{
                        zIndex: -1,
                        height:
                          toolSnapshots?.length >= 50
                            ? height - 95
                            : height - 75,
                        paddingRight: 0,
                        overflowY: "auto",
                        overflowX: "hidden",
                      }}
                      headers={[
                        {
                          key: "datetime",
                          title: t("settings.qa.date"),
                          width: 0.25,
                        },
                        {
                          key: "changes",
                          title: t("settings.qa.changes"),
                          width: 0.1,
                        },
                        {
                          key: "user",
                          title: t("settings.qa.changedBy"),
                          width: 0.3,
                        },
                        {
                          key: "semantic",
                          title: t("settings.qa.versionLabel"),
                          width: 0.2,
                        },
                        {
                          key: "version",
                          title: "#",
                          width: 0.1,
                        },
                      ]}
                      items={toolSnapshots.map((snapshot, index) => {
                        let semanticVersion = "-";
                        try {
                          const parsed = snapshot?.snapshotData
                            ? JSON.parse(snapshot.snapshotData)
                            : null;
                          if (parsed?.functionVersion) {
                            semanticVersion = `v${parsed.functionVersion}`;
                          }
                        } catch (e) {
                          semanticVersion = "-";
                        }
                        return {
                          datetime:
                            dayjs(snapshot?.updatedAt)?.format(
                              "DD-MM-YYYY HH:mm"
                            ) || " ",
                          changes: `${snapshotChangeCounts[index] || 0}`,
                          user:
                            getUserName(snapshot?.updatedBy) ||
                            getUserName(snapshot?.createdBy) ||
                            "System",
                          semantic: semanticVersion,
                          version: snapshot?.snapshotVersion?.toString() || "-",
                        };
                      })}
                    />
                  </>
                ) : (
                  <View
                    style={{
                      paddingLeft: 0,
                      alignItems: "center",
                    }}
                  >
                    <AppPlaceholderView
                      wrapperStyle={{
                        width: "80%",
                        height: "100%",
                        marginVertical: "auto",
                        marginHorizontal: "auto",
                        alignItems: "center",
                      }}
                      mainCaption="No changes recorded yet"
                      subCaption="Changes to this tool will appear here if you have a Business or an Enterprise subscription"
                      size={30}
                      icon={AppAssets.icons.orbit}
                    />
                  </View>
                )}
              </AppExpander>
            </View>
          )}
        </SectionRenderer>
      )}

      {_showBuilderSection("versions") && (
        <BareSectionRenderer>
          <View
            style={{
              zIndex: -1000,
              marginHorizontal: 10,
              marginBottom: 16,
              paddingVertical: 4,
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 8,
                marginBottom: 8,
              }}
            >
              <AppText
                preventTranslation
                style={{ fontSize: 14, fontWeight: "600" }}
                color={currentTheme?.theme?.font_color}
              >
                {t("settings.qa.versionField")}
              </AppText>
              {initialMapValues?.functionVersion ? (
                <View
                  style={{
                    paddingHorizontal: 8,
                    paddingVertical: 2,
                    borderRadius: 12,
                    backgroundColor: currentTheme?.theme?.orange,
                  }}
                >
                  <AppText
                    preventTranslation
                    style={{ fontSize: 11, fontWeight: "600" }}
                    color={currentTheme?.theme?.white}
                  >
                    v{initialMapValues?.functionVersion} (
                    {t("settings.qa.versionCurrentLabel")})
                  </AppText>
                </View>
              ) : null}
            </View>
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 10,
              }}
            >
              <View style={{ flex: 1, maxWidth: 200, marginLeft: -5 }}>
                <AppForms.AppFormField
                  showTextInputLabel={false}
                  keyboardtype="default"
                  autoCorrect={false}
                  name="functionVersion"
                  placeholder="1.2.3"
                  wrapperInputStyle={{ paddingTop: 0, marginLeft: 0 }}
                />
              </View>
              <Popable
                numberOfLines={5}
                backgroundColor={currentTheme?.theme?.tooltipBackgroundColor}
                style={[
                  {
                    zIndex: 20,
                    maxWidth: 600,
                  },
                ]}
                content={t("settings.qa.useSuggestedVersion", {
                  version: semverHelper.suggestNext(
                    initialMapValues?.functionVersion
                  ),
                })}
                action="hover"
                position="bottom"
              >
                <AppIcon
                  icon={AppAssets?.icons["planner-auto"]}
                  isClickable
                  onPress={() =>
                    setFieldValue(
                      "functionVersion",
                      semverHelper.suggestNext(
                        initialMapValues?.functionVersion
                      )
                    )
                  }
                  wrapperStyle={{
                    minWidth: 120,
                    height: Constants.appButtonHeight - 10,
                    marginTop: -10,
                  }}
                />
              </Popable>
            </View>
            <AppText
              preventTranslation
              style={{ fontSize: 11, marginTop: -8, fontStyle: "italic" }}
              color={currentTheme?.theme?.mediumGrey}
            >
              {t("settings.qa.versionSuggestedHint")}
            </AppText>
          </View>
        </BareSectionRenderer>
      )}
      {!isBuilderLayout && (
        <View style={{ alignItems: "center" }}>
          <AppForms.AppSubmitButton
            title={_title()}
            isEnabledOnFilled={!isEdit}
            awesomeIconColor={currentTheme?.theme?.white}
            backgroundColor={currentTheme?.theme?.primary}
            btnContentStyle={{
              paddingLeft: 0,
              paddingRight: 0,
            }}
            style={[
              {
                height: Constants.appButtonHeight,
                width: "auto",
                shadowColor: currentTheme?.theme?.transparent,
                marginHorizontal: 10,
              },
            ]}
            wrapperStyle={[
              {
                width: "fit-content",
                minWidth: 150,
                marginTop: 20,
                height: Constants.appButtonHeight,
              },
            ]}
          />
          {isEdit && (
            <ToothFairyButton
              title={t("common.delete")}
              onPress={handleDeletion}
              isDeletion
              wrapperStyle={{
                marginTop: 30,
                minWidth: 150,
                height: Constants.appButtonHeight,
              }}
            />
          )}
        </View>
      )}
      {_showBuilderSection("endpoint") &&
        (values?.isChatFn || values?.isHtmlFn) && (
          <BareSectionRenderer>
            <AppText
              leftIcon={AppAssets?.icons?.info}
              iconSize={14}
              wrapperStyle={{
                alignItems: "center",
                marginLeft: 10,
              }}
              color={currentTheme?.theme?.orange}
              style={{
                fontSize: currentTheme?.theme?.small_font_size,

                fontWeight: "bold",
                textAlign: "center",
                marginTop: 4,
                marginLeft: 10,
              }}
            >
              This tool is not available when agentic tooling is enabled and
              will not be used by the agent
            </AppText>
          </BareSectionRenderer>
        )}

      <ToolTestModal
        isModalOpen={showTestModal}
        setIsModalOpen={setShowTestModal}
        mode="tool"
        id={values?.id}
        token={getActiveWorkspace()?.workspaceToken}
        entityName={values?.name}
        parameters={values?.parameters}
        staticArgs={values?.staticArgs}
      />
    </>
  );
};

export default FunctionSettingsAIView;
